import styles from "../../../scss/Form.module.scss";
import useFormErrorHandling from "hooks/useFormErrorHandling";
import { Field } from "components/Shared";
import { SaveIcon } from "components/Layout/MainLayout/Sidebar/icons/SaveIcon";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Box, Card, CardContent, Button, Typography, Grid } from "@mui/material";
import { FIELDS, IPointOfSaleForm, IPointOfSaleFormProps, initialValues, pointOfSaleFormSchema } from "./Form.config";
import { useAppSelector } from "redux/store";
import { useIntl } from "react-intl";

// Image field height

const IMAGE_HEIGHT = "9rem";

/**
 * Component to render news form
 */

export const Form = (props: IPointOfSaleFormProps) => {
  const { formatMessage } = useIntl();

  const { currentPointOfSale, onSubmit, apiError, companies, provinces } = props;
  const { user } = useAppSelector((state) => state.userLoggedState);

  // Form configuration

  const methods = useForm<IPointOfSaleForm>({
    resolver: zodResolver(pointOfSaleFormSchema),
    defaultValues: initialValues(currentPointOfSale, user ?? undefined),
  });

  // Submit handler function

  const onSubmitHandler: SubmitHandler<IPointOfSaleForm> = (values: IPointOfSaleForm) => {

    const formData = new FormData();

    if (values.image) formData.append("image", values.image as Blob);
    formData.append("data", JSON.stringify(values));

    onSubmit(formData);
  };
  // Error handler hook

  useFormErrorHandling(methods.setError, apiError);


  return (
    <Card>
      <CardContent className={styles.cardContent}>
        <FormProvider {...methods}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Field.Image
                  image={currentPointOfSale?.image}
                  name={FIELDS.IMAGE.FIELD}
                  cssImage={{
                    width: "12.5rem",
                    height: IMAGE_HEIGHT,
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                  imageLoading={false}
                  height={IMAGE_HEIGHT}
                />
              </Grid>
              <Grid item xs={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field.TextField
                      name={FIELDS.NAME.FIELD}
                      label={formatMessage({ id: FIELDS.NAME.LABEL })}
                      defaultValue={currentPointOfSale?.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field.Select
                      control={methods.control}
                      name={FIELDS.COMPANY.FIELD}
                      label={formatMessage({ id: FIELDS.COMPANY.LABEL })}
                      defaultValue={user?.role !== "SUPERADMIN"
                        ? user?.companyId
                        : currentPointOfSale?.companyId}
                      multiple={false}
                      helperText={""}
                      fieldValue="value"
                      fieldName="label"
                      variant="outlined"
                      options={companies}
                      disabled={user?.role !== "SUPERADMIN"}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "address" })}
                </Typography>
              </Grid>

              <Grid item xs={2}>
                <Field.TextField
                  name={FIELDS.ADDRESS_TYPE.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_TYPE.LABEL })}
                  defaultValue={currentPointOfSale?.addressType}
                />
              </Grid>
              <Grid item xs={8}>
                <Field.TextField
                  name={FIELDS.ADDRESS_NAME.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_NAME.LABEL })}
                  defaultValue={currentPointOfSale?.addressName}
                />
              </Grid>
              <Grid item xs={2}>
                <Field.TextField
                  name={FIELDS.ADDRESS_NUMBER.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_NUMBER.LABEL })}
                  defaultValue={currentPointOfSale?.addressNumber}
                />
              </Grid>
              <Grid item xs={12}>
                <Field.TextField
                  name={FIELDS.ADDRESS_DETAILS.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_DETAILS.LABEL })}
                  defaultValue={currentPointOfSale?.addressDetails}
                />
              </Grid>
              <Grid item xs={2}>
                <Field.TextField
                  name={FIELDS.POSTAL_CODE.FIELD}
                  label={formatMessage({ id: FIELDS.POSTAL_CODE.LABEL })}
                  defaultValue={currentPointOfSale?.postalCode}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.LOCALITY.FIELD}
                  label={formatMessage({ id: FIELDS.LOCALITY.LABEL })}
                  defaultValue={currentPointOfSale?.locality}
                />
              </Grid>
              <Grid item xs={4}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.PROVINCE.FIELD}
                  label={formatMessage({ id: FIELDS.PROVINCE.LABEL })}
                  defaultValue={provinces.find((province) => province.value === currentPointOfSale?.province)}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={provinces}
                />
              </Grid>





            </Grid>

            {methods.formState.errors &&
              methods.formState.errors.general?.message && (
                <>{methods.formState.errors.general.message}</>
              )}

            <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>

                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color="#FFF"
                  alignContent="center"
                >
                  {formatMessage({ id: "save" })}
                </Typography>
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </CardContent>
    </Card>
  );
};